/**
 * Which workflow tabs the NCR page shows and where the 8D report opens
 * (QUALITY-NCR-REVIEW E2E run, 25 Sep 2026: E9, E24).
 */
import type { NonConformanceReport } from '../../lib/api';

export type EightDSubTab = 'root_cause' | 'corrective_action';
export type WorkflowTab = '8d_report' | 'collection' | 'credit' | 'replacement';

/**
 * The 8D sub-tab to open on: the CAR once the viewer's next job is writing it,
 * or once there is a CAR to read; the root cause before that.
 *
 * @param status - the NCR status
 * @param canSubmitCAR - the viewer is the one who writes the CAR now
 * @returns the sub-tab value
 */
export function eightDDefaultSubTab(status: string, canSubmitCAR: boolean): EightDSubTab {
  if (['car_submitted', 'eightd_complete', 'closed'].includes(status)) return 'corrective_action';
  if (status === 'ca_submitted' && canSubmitCAR) return 'corrective_action';
  return 'root_cause';
}

const PROCESS_TABS = [
  { tab: 'collection', plate: 'collection', flag: 'is_collection_required', process: 'collection_process' },
  { tab: 'credit', plate: 'credit', flag: 'is_credit_required', process: 'credit_process' },
  { tab: 'replacement', plate: 'replacement', flag: 'is_replacement_required', process: 'replacement_process' },
] as const;

/**
 * The tabs to show: the 8D report always, and each process that applies to
 * this NCR (the plate does not say "Not required") or has already started.
 *
 * @param ncr - the NCR
 * @returns the visible tabs in page order
 */
export function visibleWorkflowTabs(ncr: NonConformanceReport): WorkflowTab[] {
  const tabs: WorkflowTab[] = ['8d_report'];
  for (const p of PROCESS_TABS) {
    const cell = ncr.process_plate?.[p.plate];
    const applies = cell ? cell.label !== 'Not required' : !!ncr[p.flag];
    if (applies || ncr[p.process]) tabs.push(p.tab);
  }
  return tabs;
}
